import { useTranslation } from 'react-i18next'
import { type FC, type ReactNode } from 'react'
import { Pagination, PaginationControl, PaginationControlFunction, PaginationEllipsis, PaginationItem, Text, TextVariant } from '@enbw/ui-react'
import { PageSizeSelector } from './PageSizeSelector'
import styles from './styles.module.scss'

type PaginationComponentProps = {
  /**
   * Current page number (1-indexed, i.e., first page is 1)
   */
  readonly currentPage: number
  readonly totalPages: number
  readonly itemsPerPage: number
  readonly totalItems: number
  readonly pageSizeOptions?: readonly number[]
  /**
   * Called when user navigates to a different page.
   * @param page - The page number (1-indexed, i.e., first page is 1)
   */
  readonly onPageChange: (page: number) => void
  /**
   * Called when user selects a different page size.
   * Page size selector is only rendered if this prop is provided.
   * @param pageSize - The new page size selected by the user
   */
  readonly onPageSizeChange?: (pageSize: number) => void
}

export const PaginationComponent: FC<PaginationComponentProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  pageSizeOptions = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange
}) => {
  const { t } = useTranslation()

  // Calculate display indices (1-indexed for user display)
  const startIndex = (currentPage - 1) * itemsPerPage + 1
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems)

  const handlePrevious = (): void => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = (): void => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handlePageClick = (page: number): void => {
    onPageChange(page)
  }

  const createPageItem = (page: number): ReactNode => (
    <PaginationItem key={page} label={String(page)} current={page === currentPage} onClick={() => handlePageClick(page)} />
  )

  const createPageRange = (start: number, end: number): ReactNode[] => {
    const items: ReactNode[] = []
    for (let i = start; i <= end; i++) {
      items.push(createPageItem(i))
    }
    return items
  }

  const renderAllPages = (): ReactNode[] => createPageRange(1, totalPages)

  const renderPagesNearStart = (): ReactNode[] => [
    createPageItem(1),
    ...createPageRange(2, 5),
    <PaginationEllipsis key="ellipsis-end" />,
    createPageItem(totalPages)
  ]

  const renderPagesNearEnd = (): ReactNode[] => [
    createPageItem(1),
    <PaginationEllipsis key="ellipsis-start" />,
    ...createPageRange(totalPages - 4, totalPages - 1),
    createPageItem(totalPages)
  ]

  const renderPagesInMiddle = (): ReactNode[] => [
    createPageItem(1),
    <PaginationEllipsis key="ellipsis-start" />,
    ...createPageRange(currentPage - 1, currentPage + 1),
    <PaginationEllipsis key="ellipsis-end" />,
    createPageItem(totalPages)
  ]

  const renderPaginationItems = (): ReactNode[] => {
    const maxVisiblePages = 7

    if (totalPages <= maxVisiblePages) {
      return renderAllPages()
    }

    if (currentPage <= 4) {
      return renderPagesNearStart()
    }

    if (currentPage >= totalPages - 3) {
      return renderPagesNearEnd()
    }

    return renderPagesInMiddle()
  }

  return (
    <div className={styles.paginationWrapper}>
      <div className={styles.resultsInfo}>
        {totalItems > 0 && <Text variant={TextVariant.BodyExtraSmallWeighted}>{t('pagination.resultsRange', { startIndex, endIndex })}</Text>}
      </div>

      <div className={styles.paginationCenter}>
        <Pagination>
          <PaginationControl function={PaginationControlFunction.Previous} onClick={handlePrevious} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <PaginationControl function={PaginationControlFunction.Next} onClick={handleNext} disabled={currentPage === totalPages} />
        </Pagination>
      </div>

      {onPageSizeChange && <PageSizeSelector itemsPerPage={itemsPerPage} pageSizeOptions={pageSizeOptions} onPageSizeChange={onPageSizeChange} />}
    </div>
  )
}
